import {
  EyeOutlined,
  PlusOutlined,
  } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Space,
  TreeSelect,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createZnsEventBranchMap,
  deleteZnsEventBranchMap,
  getAvailableAliases,
  getListZnsEventBranchMap,
  getZnsTemplate,
  updateZnsEventBranchMap,
} from "../../../../api/znsEvent/znsEventBranchMap";
import { getListZnsEvent } from "../../../../api/znsEvent/znsEvent";
import {
  setCurrentPage,
  setPageSize,
} from "../../../../redux/znsEvent/znsEventBranchMap";
import { store } from "../../../../redux/configStore";
import useStoreData from "../../../../hooks/useStoreData";
import { transformData } from "../../../../utils/recursive";
import RowActions from "../../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../../components/cms/actions";
import { brand, neutral } from "../../../../theme/tokens";
import StatusTag from "../../../../components/cms/StatusTag";
import BrandedPageLayout from "../../../../components/Dashboard/BrandedPageLayout";
import ListToolbar from "../../../../components/cms/ListToolbar";
import CmsTableCard from "../../../../components/cms/CmsTableCard";

const { Option } = Select;
const { Text } = Typography;

const FILTER_DEFS = [
  { key: "branch", label: "Đơn vị", span: { xs: 24, sm: 12, md: 8 } },
  { key: "event", label: "Sự kiện ZNS", span: { xs: 24, sm: 12, md: 8 } },
];

// Bỏ các ký tự ngoặc [], {}, <> để key biến lưu DB sạch
const cleanParamName = (raw) => raw.replace(/[\[\]{}<>]/g, "").trim();

export default function ZnsEventBranchMapTable() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const {
    listZnsEventBranchMaps,
    isLoadZnsEventBranchMap,
    currentPage,
    pageSize,
    totalRecords,
  } = useSelector((state) => state.persistedReducer.znsEventBranchMapSlice);
  const { listZnsEvents } = useSelector(
    (state) => state.persistedReducer.znsEventSlice
  );

  const { data: allListStore, loading: isLoadStoreAll } = useStoreData();
  const branchOptions = transformData(allListStore?.data || [])?.filter(
    (item) => item.parent_id === 0
  );

  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [availableAliases, setAvailableAliases] = useState([]);

  // Bộ lọc danh sách — API `zns-branchs` đã nhận `mcoupon_branch_id`/`zns_event_id`
  // từ trước, chỉ là màn này chưa bao giờ dùng tới.
  const [branchFilter, setBranchFilter] = useState(null);
  const [eventFilter, setEventFilter] = useState(null);

  const selectedEventId = Form.useWatch("zns_event_id", form);

  const loadData = useCallback(() => {
    dispatch(
      getListZnsEventBranchMap({
        currentPage,
        pageSize,
        branchId: branchFilter,
        eventId: eventFilter,
      })
    );
  }, [dispatch, currentPage, pageSize, branchFilter, eventFilter]);

  // Đổi bộ lọc -> về trang 1, `loadData` tự chạy lại theo dependency.
  const applyFilter = (setter) => (value) => {
    setter(value || null);
    if (currentPage !== 1) dispatch(setCurrentPage(1));
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    store.dispatch(getListZnsEvent({ currentPage: 1, pageSize: 50 }));
  }, []);

  // Tải alias hệ thống theo sự kiện được chọn
  useEffect(() => {
    if (!selectedEventId) {
      setAvailableAliases([]);
      return;
    }
    (async () => {
      try {
        const res = await getAvailableAliases(selectedEventId);
        setAvailableAliases(res?.data?.data || []);
      } catch (error) {
        setAvailableAliases([]);
      }
    })();
  }, [selectedEventId]);

  const handlePreviewTemplate = async (templateId) => {
    if (!templateId) {
      message.warning("Vui lòng nhập Template ID");
      return;
    }
    try {
      setLoadingTemplate(true);
      const res = await getZnsTemplate(templateId);
      if (res?.data?.success) {
        setTemplatePreview(res.data.data);
        message.success("Tải mẫu tin thành công");
      } else {
        setTemplatePreview(null);
        message.error(res?.data?.message || "Không lấy được mẫu tin");
      }
    } catch (error) {
      setTemplatePreview(null);
      message.error(
        error?.response?.data?.message || "Không thể lấy mẫu tin"
      );
    } finally {
      setLoadingTemplate(false);
    }
  };

  const showModal = (record = null) => {
    setEditingRecord(record);
    setModalVisible(true);
    if (record) {
      form.setFieldsValue({
        mcoupon_branch_id: record.mcoupon_branch_id,
        zns_event_id: record.zns_event_id,
        template_id: record.template_id,
        oa: record.oa,
        variable: record.variable || {},
      });
      if (record.template_id) {
        handlePreviewTemplate(record.template_id);
      }
    } else {
      form.resetFields();
      setTemplatePreview(null);
      setAvailableAliases([]);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingRecord(null);
    setTemplatePreview(null);
    setAvailableAliases([]);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setModalLoading(true);
    try {
      let response;
      if (editingRecord) {
        response = await dispatch(
          updateZnsEventBranchMap({ id: editingRecord.id, data: values })
        );
      } else {
        response = await dispatch(createZnsEventBranchMap(values));
      }

      const action = editingRecord
        ? updateZnsEventBranchMap
        : createZnsEventBranchMap;

      if (action.fulfilled.match(response)) {
        if (response.payload?.data?.original) {
          message.error(response.payload.data.original.message);
        } else {
          message.success(
            editingRecord ? "Cập nhật liên kết ZNS thành công" : "Tạo liên kết ZNS thành công"
          );
          handleCancel();
          loadData();
        }
      } else {
        const errs = response.payload?.errors;
        if (errs) {
          Object.values(errs).flat().forEach((m) => message.error(m));
        } else {
          message.error(response.payload?.error || response.payload?.message || "Thao tác thất bại");
        }
      }
    } catch (error) {
      message.error("Đã xảy ra lỗi");
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      // Thunk rejected vẫn resolve -> phải soi kết quả, không thì luôn báo thành công.
      const response = await dispatch(deleteZnsEventBranchMap(id));
      if (deleteZnsEventBranchMap.fulfilled.match(response)) {
        message.success("Xóa liên kết ZNS thành công");
        loadData();
        return;
      }
      message.error(
        response.payload?.message || response.payload?.error || "Xóa liên kết ZNS thất bại"
      );
    } catch (error) {
      message.error("Có lỗi xảy ra khi xóa");
    }
  };

  const columns = [
    {
      title: "STT",
      key: "index",
      width: 60,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Chi nhánh",
      dataIndex: "mcoupon_branch_id",
      render: (_, record) => (
        <a
          style={{ color: brand.primary, fontWeight: 600, textDecoration: "none" }}
          className="hover:underline"
          onClick={() => showModal(record)}
        >
          {record.branch?.name || "—"}
        </a>
      ),
    },
    {
      title: "Sự kiện",
      dataIndex: "zns_event_id",
      render: (_, record) =>
        record.zns_event ? (
          <StatusTag>{record.zns_event.name}</StatusTag>
        ) : (
          <StatusTag tone="danger">Không xác định</StatusTag>
        ),
    },
    {
      title: "Template ID",
      dataIndex: "template_id",
      render: (text) => <StatusTag>{text}</StatusTag>,
    },
    {
      title: "OA",
      dataIndex: "oa",
      render: (text) => text || <Text type="secondary">—</Text>,
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "—"),
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 110,
      align: "center",
      render: (_, record) => (
        <RowActions
          items={[
            {
              key: "edit",
              icon: ACTION_ICON.edit,
              label: "Chỉnh sửa",
              onClick: () => showModal(record),
            },
            {
              key: "delete",
              icon: ACTION_ICON.delete,
              label: "Xóa liên kết",
              danger: true,
              divider: true,
              onClick: () =>
                confirmDelete(
                  `liên kết #${record.id}`,
                  () => handleDelete(record.id),
                  "Hành động này không thể hoàn tác."
                ),
            },
          ]}
        />
      ),
    },
  ];

  const templateParams = templatePreview?.param
    ? templatePreview.param.split(",").map((p) => cleanParamName(p)).filter(Boolean)
    : [];

  return (
    <BrandedPageLayout
      title="Liên kết sự kiện ZNS theo đơn vị"
      subtitle="Gán mẫu tin ZNS và OA gửi tin cho từng sự kiện của mỗi đơn vị, kèm bản đồ dữ liệu biến."
      breadcrumbItems={[
        { title: "Sự kiện ZNS", path: "/zns-events" },
        { title: "Liên kết đơn vị" },
      ]}
    >
      <ListToolbar
        filterDefs={FILTER_DEFS}
        filters={{
          branch: (
            <TreeSelect
              style={{ width: "100%" }}
              disabled={isLoadStoreAll}
              placeholder="Lọc theo đơn vị"
              showSearch
              allowClear
              treeLine
              value={branchFilter}
              treeData={branchOptions}
              filterTreeNode={(input, node) =>
                node.title.toLowerCase().includes(input.toLowerCase())
              }
              onChange={applyFilter(setBranchFilter)}
            />
          ),
          event: (
            <Select
              style={{ width: "100%" }}
              placeholder="Lọc theo sự kiện ZNS"
              allowClear
              showSearch
              optionFilterProp="label"
              value={eventFilter}
              options={(listZnsEvents || []).map((ev) => ({ label: ev.name, value: ev.id }))}
              onChange={applyFilter(setEventFilter)}
            />
          ),
        }}
        onHideFilter={(key) => {
          if (key === "branch") applyFilter(setBranchFilter)(null);
          if (key === "event") applyFilter(setEventFilter)(null);
        }}
        onClearFilters={() => {
          setBranchFilter(null);
          setEventFilter(null);
          if (currentPage !== 1) dispatch(setCurrentPage(1));
        }}
        loading={isLoadZnsEventBranchMap}
        onReload={loadData}
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            Thêm liên kết ZNS
          </Button>
        }
      />

      <CmsTableCard
        columns={columns}
        dataSource={isLoadZnsEventBranchMap ? [] : listZnsEventBranchMaps}
        rowKey="id"
        loading={isLoadZnsEventBranchMap}
        pagination={{
          total: totalRecords,
          current: currentPage,
          pageSize,
          onChange: (page) => dispatch(setCurrentPage(page)),
          onShowSizeChange: (_, size) => dispatch(setPageSize(size)),
        }}
        locale={{ emptyText: <Empty description="Không có dữ liệu mapping!" /> }}
        scroll={{ x: "max-content" }}
        bordered
      />

      <Drawer
          title={
            <span style={{ fontSize: 18, fontWeight: 700 }}>
              {editingRecord ? "Chỉnh sửa liên kết ZNS" : "Thêm liên kết ZNS"}
            </span>
          }
          open={modalVisible}
          onClose={handleCancel}
          // Rộng ra khi có panel xem trước mẫu tin (layout 2 cột 11/13).
          width={templatePreview ? 1100 : 640}
          // KHÔNG destroyOnClose: prefill + tải mẫu tin chạy trước khi drawer mount;
          // handleCancel đã reset form và dọn preview.
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                padding: "4px 0",
              }}
            >
              <Button onClick={handleCancel} style={{ minWidth: 80 }}>
                Hủy
              </Button>
              <Button
                type="primary"
                loading={modalLoading}
                onClick={() => form.submit()}
                style={{ minWidth: 120 }}
              >
                {editingRecord ? "Cập nhật" : "Tạo mới"}
              </Button>
            </div>
          }
        >
          <Form
            form={form}
            layout="vertical"
            requiredMark="optional"
            onFinish={handleSubmit}
            style={{ marginTop: 8 }}
          >
            <Row gutter={24}>
              <Col xs={24} lg={templatePreview ? 11 : 24}>
                <Form.Item
                  name="mcoupon_branch_id"
                  label="Đơn vị"
                  rules={[{ required: true, message: "Vui lòng chọn đơn vị" }]}
                >
                  <TreeSelect
                    allowClear
                    showSearch
                    treeLine
                    disabled={!!editingRecord || isLoadStoreAll}
                    treeData={branchOptions}
                    placeholder="Chọn đơn vị"
                    filterTreeNode={(input, node) =>
                      node.title.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="zns_event_id"
                  label="Sự kiện ZNS"
                  rules={[{ required: true, message: "Vui lòng chọn sự kiện ZNS" }]}
                >
                  <Select
                    placeholder="Chọn sự kiện ZNS"
                    showSearch
                    optionFilterProp="children"
                    disabled={!!editingRecord}
                  >
                    {(listZnsEvents || []).map((ev) => (
                      <Option key={ev.id} value={ev.id}>
                        {ev.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="template_id"
                  label="Template ID"
                  rules={[{ required: true, message: "Vui lòng nhập Template ID" }]}
                >
                  <Input
                    placeholder="Nhập Template ID"
                    onChange={(e) => {
                      if (!e.target.value) setTemplatePreview(null);
                    }}
                    addonAfter={
                      <Button
                        type="link"
                        size="small"
                        loading={loadingTemplate}
                        icon={<EyeOutlined />}
                        style={{ padding: "0 8px" }}
                        onClick={() =>
                          handlePreviewTemplate(form.getFieldValue("template_id"))
                        }
                      >
                        Xem mẫu
                      </Button>
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="oa"
                  label="OA (Official Account) gửi tin"
                  rules={[{ required: true, message: "Vui lòng nhập OA" }]}
                >
                  <Input placeholder="Nhập OA/brandname gửi tin" />
                </Form.Item>

                {templateParams.length > 0 && (
                  <>
                    <Divider orientation="left" plain style={{ fontSize: 14 }}>
                      Bản đồ dữ liệu biến
                    </Divider>
                    <div
                      style={{
                        padding: 16,
                        background: "#fafafa",
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      {templateParams.map((param) => (
                        <Form.Item
                          key={param}
                          name={["variable", param]}
                          label={
                            <span>
                              Tham số: <StatusTag>{param}</StatusTag>
                            </span>
                          }
                          style={{ marginBottom: 12 }}
                        >
                          <Select
                            placeholder="Chọn biến hệ thống cấp dữ liệu"
                            allowClear
                            showSearch
                          >
                            {availableAliases.map((alias) => (
                              <Option key={alias} value={alias}>
                                {alias}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ))}
                    </div>
                  </>
                )}
              </Col>

              {templatePreview && (
                <Col xs={24} lg={13}>
                  <Card
                    size="small"
                    title="Xem trước mẫu tin"
                    style={{ border: "2px solid #1890ff", borderRadius: 8 }}
                    headStyle={{ background: "#e6f7ff" }}
                  >
                    <div style={{ fontSize: 12, color: neutral[500], marginBottom: 8 }}>
                      Nội dung tin nhắn
                    </div>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        background: "#fff",
                        padding: 16,
                        borderRadius: 6,
                        border: "1px solid #d9d9d9",
                        height: 220,
                        overflowY: "auto",
                        fontSize: 14,
                        lineHeight: 1.6,
                      }}
                    >
                      {templatePreview.content}
                    </div>
                    {templateParams.length > 0 && (
                      <>
                        <div style={{ fontSize: 12, color: neutral[500], margin: "12px 0 8px" }}>
                          Tham số động ({templateParams.length})
                        </div>
                        <Space wrap>
                          {templateParams.map((param) => (
                            <StatusTag key={param}>
                              {param}
                            </StatusTag>
                          ))}
                        </Space>
                      </>
                    )}
                  </Card>
                </Col>
              )}
            </Row>
          </Form>
        </Drawer>
    </BrandedPageLayout>
  );
}
